import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { User, Mail, Lock, Building2, Save, ShieldCheck, ShieldOff } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../components/Toast';
import api, { setupMfa, enableMfa, disableMfa } from '../services/api';

export default function ProfilePage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const toast = useToast();
  const [name, setName] = useState(user?.name || '');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [saving, setSaving] = useState(false);

  // MFA (TOTP)
  const [mfaEnabled, setMfaEnabled] = useState(!!user?.mfa_enabled);
  const [mfaSetup, setMfaSetup] = useState(null); // { qr, secret }
  const [mfaCode, setMfaCode] = useState('');
  const [showDisable, setShowDisable] = useState(false);
  const [disablePassword, setDisablePassword] = useState('');
  const [mfaBusy, setMfaBusy] = useState(false);

  const startMfaSetup = async () => {
    setMfaBusy(true);
    try {
      const res = await setupMfa();
      setMfaSetup({ qr: res.data.qr, secret: res.data.secret });
      setMfaCode('');
    } catch (err) { toast.error(err.response?.data?.error || t('common.error')); }
    finally { setMfaBusy(false); }
  };

  const confirmMfa = async () => {
    if (mfaCode.length < 6) return;
    setMfaBusy(true);
    try {
      await enableMfa(mfaCode);
      setMfaEnabled(true);
      setMfaSetup(null);
      setMfaCode('');
      const stored = JSON.parse(localStorage.getItem('user') || '{}');
      localStorage.setItem('user', JSON.stringify({ ...stored, mfa_enabled: true }));
      toast.success(t('profile.mfaEnabledToast'));
    } catch (err) { toast.error(err.response?.data?.error || t('common.error')); }
    finally { setMfaBusy(false); }
  };

  const cancelMfaSetup = () => { setMfaSetup(null); setMfaCode(''); };

  const handleDisableMfa = async () => {
    if (!disablePassword) return;
    setMfaBusy(true);
    try {
      await disableMfa(disablePassword);
      setMfaEnabled(false);
      setShowDisable(false);
      setDisablePassword('');
      const stored = JSON.parse(localStorage.getItem('user') || '{}');
      localStorage.setItem('user', JSON.stringify({ ...stored, mfa_enabled: false }));
      toast.success(t('profile.mfaDisabledToast'));
    } catch (err) { toast.error(err.response?.data?.error || t('common.error')); }
    finally { setMfaBusy(false); }
  };

  const handleSaveName = async () => {
    if (!name.trim()) return;
    setSaving(true);
    try {
      await api.put(`/users/${user.id}`, { name });
      toast.success(t('profile.nameSaved'));
    } catch (err) { toast.error(err.response?.data?.error || t('common.error')); }
    finally { setSaving(false); }
  };

  const handleChangePassword = async () => {
    if (!currentPassword || !newPassword) return;
    if (newPassword.length < 6) { toast.error(t('profile.passwordMinLength')); return; }
    if (newPassword !== confirmPassword) { toast.error(t('profile.passwordMismatch')); return; }
    setSaving(true);
    try {
      await api.put(`/users/${user.id}`, { current_password: currentPassword, new_password: newPassword });
      toast.success(t('profile.passwordChanged'));
      setCurrentPassword(''); setNewPassword(''); setConfirmPassword('');
    } catch (err) { toast.error(err.response?.data?.error || t('profile.passwordChangeError')); }
    finally { setSaving(false); }
  };

  const ic = "w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent";

  return (
    <div className="p-6">
      <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-3 mb-6">
        <User size={28} className="text-blue-600" /> {t('profile.title')}
      </h1>

      {/* Info */}
      <div className="bg-white border border-gray-200 rounded-lg p-6 mb-6">
        <div className="flex items-center gap-4 mb-6">
          <div className="w-16 h-16 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-2xl font-bold">
            {(user?.name || '?')[0].toUpperCase()}
          </div>
          <div>
            <div className="text-lg font-bold text-gray-800">{user?.name}</div>
            <div className="text-sm text-gray-500">{user?.email}</div>
            <div className="text-xs text-gray-400 flex items-center gap-2 mt-1">
              <span className="px-2 py-0.5 bg-gray-100 rounded">{user?.role}</span>
              <span className="flex items-center gap-1"><Building2 size={12} /> {user?.tenant_name}</span>
            </div>
          </div>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('profile.nameLabel')}</label>
            <div className="flex gap-2">
              <input type="text" value={name} onChange={e => setName(e.target.value)} className={ic} />
              <button onClick={handleSaveName} disabled={saving || name === user?.name}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-40 text-white rounded-lg text-sm flex items-center gap-1 flex-shrink-0">
                <Save size={14} /> {t('common.save')}
              </button>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('profile.emailLabel')}</label>
            <div className="flex items-center gap-2">
              <Mail size={16} className="text-gray-400" />
              <span className="text-sm text-gray-600">{user?.email}</span>
              <span className="text-xs text-gray-400">{t('profile.emailReadonly')}</span>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('profile.companyLabel')}</label>
            <div className="flex items-center gap-2">
              <Building2 size={16} className="text-gray-400" />
              <span className="text-sm text-gray-600">{user?.tenant_name}</span>
              <span className="text-xs text-gray-400">{t('profile.planLabel', { plan: user?.tenant_plan })}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Change password */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <h2 className="text-lg font-bold text-gray-800 flex items-center gap-2 mb-4">
          <Lock size={20} className="text-gray-600" /> {t('profile.changePasswordTitle')}
        </h2>
        <div className="space-y-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('profile.currentPassword')}</label>
            <input type="password" value={currentPassword} onChange={e => setCurrentPassword(e.target.value)} className={ic} />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('profile.newPassword')}</label>
            <input type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)} className={ic} />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('profile.confirmPassword')}</label>
            <input type="password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} className={ic} />
          </div>
          <button onClick={handleChangePassword} disabled={saving || !currentPassword || !newPassword}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-40 text-white rounded-lg text-sm flex items-center gap-1">
            <Lock size={14} /> {t('profile.changePasswordButton')}
          </button>
        </div>
      </div>

      {/* Two-factor authentication (MFA / TOTP) */}
      <div className="bg-white border border-gray-200 rounded-lg p-6 mt-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-bold text-gray-800 flex items-center gap-2">
            <ShieldCheck size={20} className="text-gray-600" /> {t('profile.mfaTitle')}
          </h2>
          {mfaEnabled ? (
            <span className="px-2.5 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium flex items-center gap-1">
              <ShieldCheck size={12} /> {t('profile.mfaStatusOn')}
            </span>
          ) : (
            <span className="px-2.5 py-1 bg-gray-100 text-gray-500 rounded-full text-xs font-medium flex items-center gap-1">
              <ShieldOff size={12} /> {t('profile.mfaStatusOff')}
            </span>
          )}
        </div>
        <p className="text-sm text-gray-500 mb-4">{t('profile.mfaDescription')}</p>

        {/* Enabled → option to disable */}
        {mfaEnabled && !showDisable && (
          <button onClick={() => setShowDisable(true)}
            className="px-4 py-2 border border-red-300 text-red-600 hover:bg-red-50 rounded-lg text-sm flex items-center gap-1">
            <ShieldOff size={14} /> {t('profile.mfaDisableButton')}
          </button>
        )}
        {mfaEnabled && showDisable && (
          <div className="space-y-3 max-w-sm">
            <p className="text-sm text-gray-600">{t('profile.mfaDisableConfirm')}</p>
            <input type="password" value={disablePassword} onChange={e => setDisablePassword(e.target.value)}
              placeholder={t('profile.currentPassword')} className={ic} />
            <div className="flex gap-2">
              <button onClick={handleDisableMfa} disabled={mfaBusy || !disablePassword}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 disabled:opacity-40 text-white rounded-lg text-sm">
                {t('profile.mfaDisableButton')}
              </button>
              <button onClick={() => { setShowDisable(false); setDisablePassword(''); }}
                className="px-4 py-2 border border-gray-300 text-gray-600 hover:bg-gray-50 rounded-lg text-sm">
                {t('common.cancel')}
              </button>
            </div>
          </div>
        )}

        {/* Disabled → start setup */}
        {!mfaEnabled && !mfaSetup && (
          <button onClick={startMfaSetup} disabled={mfaBusy}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-40 text-white rounded-lg text-sm flex items-center gap-1">
            <ShieldCheck size={14} /> {t('profile.mfaEnableButton')}
          </button>
        )}

        {/* Setup in progress → QR + code */}
        {!mfaEnabled && mfaSetup && (
          <div className="space-y-4 max-w-sm">
            <ol className="text-sm text-gray-600 list-decimal list-inside space-y-1">
              <li>{t('profile.mfaStep1')}</li>
              <li>{t('profile.mfaStep2')}</li>
              <li>{t('profile.mfaStep3')}</li>
            </ol>
            <div className="flex flex-col items-center gap-3 p-4 bg-gray-50 border border-gray-200 rounded-lg">
              <img src={mfaSetup.qr} alt="QR" className="w-44 h-44" />
              <div className="text-center">
                <div className="text-xs text-gray-400 mb-1">{t('profile.mfaManualKey')}</div>
                <code className="text-xs bg-white border border-gray-200 px-2 py-1 rounded break-all">{mfaSetup.secret}</code>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('profile.mfaCodeLabel')}</label>
              <input type="text" inputMode="numeric" value={mfaCode}
                onChange={e => setMfaCode(e.target.value.replace(/[^0-9]/g, '').slice(0, 6))}
                placeholder="000000"
                className={`${ic} text-center tracking-[0.4em]`} />
            </div>
            <div className="flex gap-2">
              <button onClick={confirmMfa} disabled={mfaBusy || mfaCode.length < 6}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-40 text-white rounded-lg text-sm">
                {t('profile.mfaConfirmButton')}
              </button>
              <button onClick={cancelMfaSetup}
                className="px-4 py-2 border border-gray-300 text-gray-600 hover:bg-gray-50 rounded-lg text-sm">
                {t('common.cancel')}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
